/**
 * SmartInstallBanner — friction-killing PWA install nudge.
 *
 * Detects the user's situation and shows the most helpful banner:
 *   • iPhone Safari, not installed       → animated arrow pointing to Safari's
 *                                          Share button + "Tap Share, then
 *                                          Add to Home Screen" coaching
 *   • iPhone Chrome / Firefox / FB / etc → "Open in Safari to install"
 *                                          (Apple blocks PWA install in any
 *                                          other iOS browser)
 *   • Android Chrome, install available  → one-tap "Install" button using
 *                                          the captured beforeinstallprompt
 *   • Desktop Chrome/Edge, install avail → same one-click install
 *   • Already installed (standalone)     → hidden
 *   • Dismissed in last 7 days           → hidden
 *
 * Mount once in App.js (renders globally above all routes). Self-positions
 * at the bottom of the viewport, above the iOS home indicator + above the
 * mobile bottom nav.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Plus, Download, X, ArrowDown, Apple, Chrome } from "lucide-react";

const DISMISS_KEY = "install_banner_dismissed_until";
const DISMISS_DAYS = 7;

function detect() {
    const ua = navigator.userAgent || "";
    const isIPad = /iPad/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isIPhone = /iPhone|iPod/.test(ua);
    const isIOS = isIPhone || isIPad;
    const isAndroid = /Android/i.test(ua);
    // iOS Safari is the *only* iOS browser that allows PWA install.
    // Detect by: iOS + not Chrome/Firefox/FB-app/Instagram-app/etc.
    const isIOSSafari = isIOS &&
        /Safari/.test(ua) &&
        !/CriOS|FxiOS|EdgiOS|FBAN|FBAV|Instagram|Line|MicroMessenger/i.test(ua);
    const isIOSOtherBrowser = isIOS && !isIOSSafari;
    const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
    return { isIOS, isIPhone, isIPad, isAndroid, isIOSSafari, isIOSOtherBrowser, isStandalone };
}

function isDismissed() {
    try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        return until && Date.now() < until;
    } catch {
        return false;
    }
}

function dismissFor(days = DISMISS_DAYS) {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400 * 1000));
    } catch {
        /* noop */
    }
}

export default function SmartInstallBanner() {
    const [platform, setPlatform] = useState(null);
    const [deferred, setDeferred] = useState(null);
    const [dismissed, setDismissed] = useState(true); // start hidden, reveal once detected
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const p = detect();
        setPlatform(p);
        if (p.isStandalone) return;
        if (isDismissed()) return;
        // Reveal after a brief delay so it doesn't feel intrusive
        const t = setTimeout(() => setDismissed(false), 1500);

        const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
        const onInstalled = () => setDismissed(true);
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            clearTimeout(t);
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const close = () => {
        dismissFor();
        setDismissed(true);
    };

    const triggerInstall = async () => {
        if (!deferred) return;
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") setDismissed(true);
        setDeferred(null);
    };

    const variant = useMemo(() => {
        if (!platform) return null;
        if (platform.isStandalone) return null;
        if (deferred) return "one-tap";
        if (platform.isIOSSafari) return "ios-safari";
        if (platform.isIOSOtherBrowser) return "ios-wrong-browser";
        return null;
    }, [platform, deferred]);

    if (dismissed || !variant) return null;

    return (
        <div
            data-testid="install-banner"
            className="fixed left-0 right-0 bottom-0 z-[60] pointer-events-none"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
            <div className="pointer-events-auto mx-auto max-w-2xl m-3 md:m-4
                            bg-gradient-to-br from-[#0A1128] to-[#1A6177] text-white
                            rounded-2xl shadow-2xl border border-[#C5A059]/30
                            overflow-hidden">
                {variant === "one-tap" && (
                    <div className="p-3 md:p-4 flex items-center gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-[#C5A059] flex items-center justify-center">
                            <img src="/icons/mascot-192.png" alt="Tax Solution" className="w-full h-full object-cover rounded-xl" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm leading-tight">Install Tax Solution</div>
                            <div className="text-[11px] text-white/70 leading-tight">Get it on your home screen — one tap</div>
                        </div>
                        <button onClick={triggerInstall} data-testid="install-banner-android-btn"
                            className="bg-[#C5A059] hover:bg-[#D4B574] text-[#0A1128] font-bold text-sm px-4 py-2 rounded-full flex items-center gap-1.5">
                            <Download size={14} /> Install
                        </button>
                        <button onClick={close} aria-label="Dismiss"
                            data-testid="install-banner-close"
                            className="shrink-0 text-white/60 hover:text-white p-1">
                            <X size={18} />
                        </button>
                    </div>
                )}

                {variant === "ios-safari" && (
                    <div className="p-3 md:p-4" data-testid="install-banner-ios">
                        <div className="flex items-center gap-3">
                            <div className="shrink-0 w-10 h-10 rounded-xl bg-[#C5A059] flex items-center justify-center">
                                <img src="/icons/mascot-192.png" alt="Tax Solution" className="w-full h-full object-cover rounded-xl" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm leading-tight flex items-center gap-1">
                                    <Apple size={14} /> Add to Home Screen
                                </div>
                                <div className="text-[11px] text-white/70 leading-tight">
                                    {expanded
                                        ? "Tap the Share button, then \u201CAdd to Home Screen\u201D"
                                        : "Get Tax Solution as an app — takes 3 taps"}
                                </div>
                            </div>
                            <button onClick={() => setExpanded((v) => !v)}
                                data-testid="install-banner-ios-show"
                                className="bg-[#C5A059] hover:bg-[#D4B574] text-[#0A1128] font-bold text-xs px-3 py-2 rounded-full">
                                {expanded ? "Hide" : "Show me how"}
                            </button>
                            <button onClick={close} aria-label="Dismiss"
                                className="shrink-0 text-white/60 hover:text-white p-1">
                                <X size={18} />
                            </button>
                        </div>

                        {expanded && (
                            <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                                <div className="flex items-start gap-2 text-xs">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#C5A059] text-[#0A1128] font-bold flex items-center justify-center text-[10px]">1</span>
                                    <div className="flex-1">
                                        Tap the <span className="inline-flex items-center gap-0.5 bg-white/10 px-1.5 py-0.5 rounded text-[10px]"><Share2 size={11} /> Share</span> button at the bottom of Safari
                                        <div className="text-white/50 text-[10px]">(square with an arrow pointing up)</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2 text-xs">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#C5A059] text-[#0A1128] font-bold flex items-center justify-center text-[10px]">2</span>
                                    <div className="flex-1">
                                        Scroll down and tap <span className="inline-flex items-center gap-0.5 bg-white/10 px-1.5 py-0.5 rounded text-[10px]"><Plus size={11} /> Add to Home Screen</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2 text-xs">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-[#C5A059] text-[#0A1128] font-bold flex items-center justify-center text-[10px]">3</span>
                                    <div className="flex-1">Tap <strong>Add</strong> in the top-right corner. Done!</div>
                                </div>
                                <Link to="/install" onClick={close}
                                    className="block text-center text-[11px] text-[#C5A059] underline pt-1">
                                    See full guide with screenshots →
                                </Link>
                            </div>
                        )}

                        {/* Animated arrow pointing at iOS Safari's bottom Share bar */}
                        {expanded && (
                            <div className="flex justify-center mt-2 animate-bounce text-[#C5A059]">
                                <ArrowDown size={28} />
                            </div>
                        )}
                    </div>
                )}

                {variant === "ios-wrong-browser" && (
                    <div className="p-3 md:p-4 flex items-center gap-3" data-testid="install-banner-ios-wrong">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-[#C5A059] flex items-center justify-center">
                            <Chrome size={20} className="text-[#0A1128]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm leading-tight">Open in Safari to install</div>
                            <div className="text-[11px] text-white/70 leading-tight">
                                Apple only allows PWA install from Safari on iPhone. Copy the link → open Safari → paste.
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(window.location.origin);
                                    /* don't await toast — banner has its own UX */
                                } catch { /* noop */ }
                            }}
                            data-testid="install-banner-copy-url"
                            className="bg-[#C5A059] hover:bg-[#D4B574] text-[#0A1128] font-bold text-xs px-3 py-2 rounded-full">
                            Copy link
                        </button>
                        <button onClick={close} aria-label="Dismiss"
                            className="shrink-0 text-white/60 hover:text-white p-1">
                            <X size={18} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
